import { WorkspaceDocument } from "../types";

// `[[Target]]` or `[[Target|Alias]]`. Target excludes [ ] | ; alias excludes [ ].
export const WIKILINK_RE = /\[\[([^[\]|]+)(?:\|([^[\]]+))?\]\]/g;

// `#tag` — must start with a letter, then word chars / - / / (nested tags).
// Lookbehind keeps it from matching `#` in the middle of a word or a URL.
export const TAG_RE = /(?<=^|\s)#([A-Za-z][A-Za-z0-9_/-]*)/g;

const norm = (name: string) => name.trim().toLowerCase();

/** Distinct note names referenced by `[[wikilinks]]` in `content`. */
export const extractWikiTargets = (content: string): string[] => {
  const targets = new Set<string>();
  for (const match of content.matchAll(WIKILINK_RE)) {
    const target = match[1].trim();
    if (target) targets.add(target);
  }
  return [...targets];
};

/** Distinct `#tags` in `content`, without the leading `#`. */
export const extractTags = (content: string): string[] => {
  const tags = new Set<string>();
  for (const match of content.matchAll(TAG_RE)) tags.add(match[1]);
  return [...tags];
};

/** Lower-cased set of every note name in the vault — for "does this link resolve?". */
export const noteNameSet = (documents: WorkspaceDocument[]): Set<string> =>
  new Set(documents.map((doc) => norm(doc.name)));

/** True when `content` contains a `[[wikilink]]` pointing at `targetName`. */
export const linksTo = (content: string, targetName: string): boolean => {
  const target = norm(targetName);
  return extractWikiTargets(content).some((name) => norm(name) === target);
};

/** Notes that link to `targetName` (excluding the note itself). */
export const findBacklinks = (
  documents: WorkspaceDocument[],
  targetName: string,
  selfId: string,
): WorkspaceDocument[] =>
  documents.filter((doc) => doc.id !== selfId && linksTo(doc.content, targetName));

/** Every tag across the vault, with how many notes use it, sorted by frequency. */
export const collectVaultTags = (documents: WorkspaceDocument[]): { tag: string; count: number }[] => {
  const counts = new Map<string, number>();
  for (const doc of documents) {
    for (const tag of extractTags(doc.content)) counts.set(tag, (counts.get(tag) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([tag, count]) => ({ tag, count }))
    .sort((a, b) => b.count - a.count || a.tag.localeCompare(b.tag));
};

/** Find a note by name, case-insensitively. */
export const findNoteByName = (
  documents: WorkspaceDocument[],
  name: string,
): WorkspaceDocument | undefined => {
  const target = norm(name);
  return documents.find((doc) => norm(doc.name) === target);
};
